import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { useAuditDetail } from '@/app/(client)/audits/-hooks/useAuditDetail';
import { useAuditSocialScoreMatches } from '@/app/(client)/audits/-hooks/useAuditSocialScoreMatches';
import { AuditFindingsTable } from '@/components/shared/AuditFindingsTable';
import { useTranslator } from '@/hooks/useTranslator';

const searchSchema = z.object({
  page: z.number().int().min(1).optional(),
  sort: z.string().optional(),
  /** Finding to open on arrival — the score tab's itemized lines link here. */
  findingId: z.string().optional(),
});

export const Route = createFileRoute(
  '/(client)/audits/(pages)/(detail)/$auditId/findings/',
)({
  validateSearch: searchSchema,
  component: AuditFindingsPage,
});

/** The audit's findings — one table, whether or not a template has been
 *  applied.
 *
 *  ── WHY THERE IS NO LONGER A SOURCE SWITCH ───────────────────────────────
 *
 *  This tab used to offer three readings of the same findings behind a
 *  switch: the audit's own, the template's, and both merged. All three listed
 *  the same records, so the switch asked the reader to pick a vocabulary
 *  rather than a subject — and the template's table, having no CAPA record
 *  behind it, could show neither status nor tags nor open a detail sheet.
 *
 *  Applying a template does not change which findings exist; it adds a reading
 *  of each one. So it adds a column instead of a table, and the audit's grade
 *  and the template's sit on one row where they can be compared. */
function AuditFindingsPage() {
  const { auditId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { getTranslation: t } = useTranslator({ page: 'auditDetail' });
  const { data: audit } = useAuditDetail(auditId);
  // Served by the backend's newest completed run, not computed in this
  // browser: the grades used to live in `auditBenchmarkStore`, so a colleague
  // opening the same audit saw an empty column.
  const { regrades } = useAuditSocialScoreMatches(auditId);

  // One source for what the template calls each grade — the score card reads
  // the same keys, so the column and the card cannot disagree.
  const templateSeverityLabels = {
    zeroTolerance: t('benchmark.severity.zeroTolerance'),
    critical: t('benchmark.severity.critical'),
    major: t('benchmark.severity.major'),
    moderate: t('benchmark.severity.moderate'),
    minor: t('benchmark.severity.minor'),
  };

  if (!audit) return null;

  return (
    <AuditFindingsTable
      companyId={audit.companyId ?? ''}
      auditId={auditId}
      {...(audit.name != null ? { auditName: audit.name } : {})}
      {...(audit.companyName != null ? { companyName: audit.companyName } : {})}
      page={search.page ?? 1}
      {...(search.findingId !== undefined
        ? { initialFindingId: search.findingId }
        : {})}
      onPageChange={next =>
        void navigate({
          search: prev => ({
            ...prev,
            // Page 1 is the default — keep it out of the URL.
            page: next === 1 ? undefined : next,
          }),
          replace: true,
        })
      }
      {...(search.sort !== undefined ? { sort: search.sort } : {})}
      templateFindings={regrades}
      templateSeverityLabels={templateSeverityLabels}
    />
  );
}
